// Yapıştırma durum makinesi (TODO-KPI-05): paste olayı → plan (saf) → önizleme → onayda sırayla
// kaydet (toplu `onSaveCells` varsa tek refresh; yoksa tekil `onSaveCell` ardışık) → 5 sn snackbar +
// "Geri al" (eski değerleri yeniden yazar). KpiGrid yalnız bağlar; plan mantığı lib'de test edilir.

import { useCallback, useEffect, useRef, useState } from "react";
import type { CellInput, CellPos, GridColumn, GridRow } from "../../lib/kpi-grid-nav";
import {
  PASTE_COPY,
  planPaste,
  undoWrites,
  type PastePlan,
  type PasteWrite,
} from "../../lib/kpi-grid-paste";

export type PasteSaveItem = { metricId: string; entityId: string; year: number } & CellInput;

export interface UseGridPasteArgs {
  rows: GridRow[];
  columns: GridColumn[];
  focus: CellPos | null;
  editing: boolean;
  readOnly: boolean;
  clientView: boolean;
  onSaveCell: (args: PasteSaveItem) => void;
  onSaveCells?: ((items: PasteSaveItem[]) => Promise<{ ok: number; failed: number }>) | undefined;
}

export interface GridPasteState {
  plan: PastePlan | null;
  busy: boolean;
  snack: { message: string; undo: PasteWrite[] | null } | null;
  onPaste: (e: React.ClipboardEvent) => void;
  confirm: () => void;
  cancel: () => void;
  undo: () => void;
  /** DR12 "Boş satırı atla": aynı pano metnini boş satırlar hedef tüketmeden yeniden planlar */
  skipEmptyRows: () => void;
}

const SNACK_MS = 5000;

export function useGridPaste(args: UseGridPasteArgs): GridPasteState {
  const { rows, columns, focus, editing, readOnly, clientView, onSaveCell, onSaveCells } = args;
  const [plan, setPlan] = useState<PastePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<GridPasteState["snack"]>(null);
  const timer = useRef<number | null>(null);
  const source = useRef<{ text: string; focus: CellPos } | null>(null);

  useEffect(() => {
    if (!snack) return;
    timer.current = window.setTimeout(() => setSnack(null), SNACK_MS);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [snack]);

  const onPaste = useCallback(
    (e: React.ClipboardEvent): void => {
      if (editing || readOnly || !focus || plan) return;
      const text = e.clipboardData.getData("text/plain");
      if (!text.trim()) return;
      e.preventDefault();
      source.current = { text, focus };
      setPlan(planPaste({ rows, columns, focus, text, clientView }));
    },
    [editing, readOnly, focus, plan, rows, columns, clientView],
  );

  const apply = useCallback(
    async (writes: PasteWrite[]): Promise<{ ok: number; failed: number }> => {
      const items: PasteSaveItem[] = writes.map((w) => ({
        metricId: w.metricId,
        entityId: w.entityId,
        year: w.year,
        ...w.input,
      }));
      if (onSaveCells) return onSaveCells(items);
      for (const it of items) onSaveCell(it);
      return { ok: items.length, failed: 0 };
    },
    [onSaveCell, onSaveCells],
  );

  const confirm = useCallback((): void => {
    if (!plan || plan.writes.length === 0 || busy) return;
    const writes = plan.writes;
    setBusy(true);
    void apply(writes)
      .then((r) => {
        setSnack({
          message: r.failed > 0 ? PASTE_COPY.partial(r.ok, r.failed) : PASTE_COPY.done(r.ok),
          undo: undoWrites(writes),
        });
      })
      .finally(() => {
        setBusy(false);
        setPlan(null);
      });
  }, [plan, busy, apply]);

  const cancel = useCallback((): void => {
    if (!busy) setPlan(null);
  }, [busy]);

  const undo = useCallback((): void => {
    const writes = snack?.undo;
    if (!writes) return;
    setSnack(null);
    void apply(writes).then(() => setSnack({ message: PASTE_COPY.undone, undo: null }));
  }, [snack, apply]);

  const skipEmptyRows = useCallback((): void => {
    const src = source.current;
    if (!src || busy) return;
    setPlan(planPaste({ rows, columns, ...src, clientView, skipEmptySourceRows: true }));
  }, [busy, rows, columns, clientView]);

  return { plan, busy, snack, onPaste, confirm, cancel, undo, skipEmptyRows };
}
